import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory, Redirect, useLocation } from 'react-router-dom';
import AppLayout from 'layouts/app-layout';
import { PageTitle, Loading, SkeletonText } from 'components/atoms';
import { useFetch } from 'hooks';
import { useConfig } from 'config';
import { useAuth } from 'auth';
import { useTerminologyConfig } from 'context/DataContext';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from 'services/applicationInsightsService';
import { ThirdPartyEditInternalOwners } from 'components/organisms/third-party-internal-owners-edit';
import {
  getThirdPartyConfiguration,
  ThirdPartyDataConfigDictionary,
} from 'services/getThirdPartyConfiguration';
import { Breadcrumbs } from 'components/molecules';
import { BreadcrumbItem } from 'components/molecules/breadcrumbs/types';
import { ThirdPartyRouteInfo } from 'types/router';

const ThirdPartyEditInternalOwnersPage = () => {
  const { id } = useParams<{ id?: string }>();
  const { tokenInfo } = useAuth();
  const terminologyConfig = useTerminologyConfig();
  const { vantageWebApi, msalSubscriptionKey, xFunctionsKey } = useConfig();
  const history = useHistory();
  const location = useLocation<{
    mainThirdParty: ThirdPartyRouteInfo;
    linkedThirdParty: ThirdPartyRouteInfo;
  }>();
  const locationState = location.state;
  const [thirdPartyDataConfig, setThirdPartyDataConfig] =
    useState<ThirdPartyDataConfigDictionary>(null);

  const headers = useMemo(
    () => ({
      'Ocp-Apim-Subscription-Key': msalSubscriptionKey,
      'X-functions-Key': xFunctionsKey,
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenInfo.accessToken}`,
    }),
    [msalSubscriptionKey, xFunctionsKey, tokenInfo.accessToken]
  );

  const {
    data: thirdParty,
    loading,
    responseStatus,
  } = useFetch(`${vantageWebApi}/thirdParty/${id}`, {
    method: 'GET',
    headers,
  });

  useEffect(() => {
    if (!thirdParty?.type) return;

    const fetchDependentData = async () => {
      getThirdPartyConfiguration(vantageWebApi, headers, thirdParty.type).then((config) =>
        setThirdPartyDataConfig(config)
      );
    };

    fetchDependentData();
    // eslint-disable-next-line
  }, [thirdParty?.type, tokenInfo.accessToken]);

  if (responseStatus === 404) {
    return <Redirect to="/not-found" />;
  }
  if (responseStatus === 401) {
    return <Redirect to="/unauthorised" />;
  }

  const handleComplete = () => {
    history.push(`/third-party/profile/${id}`);
  };

  const getBreadcrumb = (): BreadcrumbItem[] => {
    if (locationState?.linkedThirdParty) {
      return [
        {
          label: terminologyConfig?.thirdPartySingularNaming + ' manager',
          route: '/',
        },
        {
          label: locationState.mainThirdParty.name,
          route: `/third-party/profile/${locationState.mainThirdParty.id}`,
        },
        {
          label: locationState.linkedThirdParty.name,
          route: `/third-party/profile/${locationState.linkedThirdParty.id}`,
        },
        {
          label: `Edit ${locationState.linkedThirdParty.name} Owners`,
        },
      ] as BreadcrumbItem[];
    } else {
      return [
        {
          label: terminologyConfig?.thirdPartySingularNaming + ' manager',
          route: '/',
        },
        {
          label: thirdParty?.name,
          route: `/third-party/profile/${id}`,
        },
        {
          label: `Edit ${thirdParty?.name} Owners`,
        },
      ] as BreadcrumbItem[];
    }
  };

  return (
    <AppLayout topBarSlot={loading ? <SkeletonText /> : <Breadcrumbs items={getBreadcrumb()} />}>
      {loading || !thirdParty || !thirdPartyDataConfig ? (
        <Loading />
      ) : (
        <>
          <PageTitle title="Edit Internal Owners" />
          <ThirdPartyEditInternalOwners
            onCancel={handleComplete}
            onSave={handleComplete}
            id={id}
            entityType={thirdParty.type}
            thirdPartyOwners={thirdParty.thirdPartyOwners}
            thirdPartyDataConfig={thirdPartyDataConfig}
            headers={headers}
            inModal={false}
          />
        </>
      )}
    </AppLayout>
  );
};

export default withAITracking(reactPlugin, ThirdPartyEditInternalOwnersPage);
